import { Icon, IconButton, Link, Typography, Visible } from '@vp/swan'
import type { IconProps } from '@vp/swan'
import type { AnchorHTMLAttributes, DetailedHTMLProps } from 'react'
import { getHeaderTracker } from '../../../tracking'
import { TEXT_LAYOUT_LINK } from '../../../constants/analytics'

interface LinkWithIconProps extends DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement> {
  iconType: IconProps['iconType']
  label: React.ReactNode
  analyticsId?: string
  accessibleLabel?: string
}

const LinkWithIcon: React.FC<LinkWithIconProps> = ({ iconType, label, analyticsId, accessibleLabel, ...linkProps }) => {
  const handleTracking = () => {
    if (analyticsId || (typeof label === 'string' && label)) {
      getHeaderTracker().trackLink({
        navigationDetailPath: [analyticsId || (typeof label === 'string' ? label : '')],
        href: linkProps.href,
        linkType: TEXT_LAYOUT_LINK,
      })
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleTracking()
    linkProps.onClick?.(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleTracking()
      linkProps.onKeyDown?.(e)
    }
  }

  const ariaLabel = accessibleLabel ?? (typeof label === 'string' ? label : '')

  return (
    <>
      <Visible xs sm md>
        <IconButton
          skin="tertiary"
          tooltip={ariaLabel}
          render={(props, ref) => (
            <Link ref={ref} {...props} {...linkProps} aria-label={ariaLabel} onClick={handleClick} onKeyDown={handleKeyDown}>
              {props.children}
            </Link>
          )}
          ml={{ sm: 'to-actions' }}
        >
          <Icon iconType={iconType} size="standard" aria-hidden="true" />
        </IconButton>
      </Visible>
      <Visible xl lg>
        <Link
          withIcon
          fontSkin="body-small"
          ml="to-actions"
          skin="unstyled"
          {...linkProps}
          aria-label={ariaLabel}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          <Icon iconType={iconType} size="standard" aria-hidden="true" />
          {/* Added margin-left to reduce CLS for cart icon as it has count which on CSR which adds extra space of between-icon-and-text */}
          <Typography {...(iconType === 'cart' ? { ml: 'between-icon-and-text' } : {})}>{label}</Typography>
        </Link>
      </Visible>
    </>
  )
}

export default LinkWithIcon
